// components/Popup/PopupBooking.jsx (Updated with better form design)
import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { useUser } from '../../context/UserContext';

import {
  acceptBooking,
  cancelBooking,
  updateBookingStatus,
  addMyNotes as addMyNotesAPI,
} from '../../services/api';
import './PopupBooking.scss';

const PopupBooking = ({
  isOpen,
  onClose,
  bookingDetails,
  onStatusUpdate,
  clientId,
  calendarId,
  onBookingCreated,
  onBookingDeleted
}) => {
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [myNotes, setMyNotes] = useState('');
  const [isNewBooking, setIsNewBooking] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const { user, setUser } = useUser();

  // Initialize state when bookingDetails changes
  useEffect(() => {
    if (bookingDetails) {
      const isNew = !bookingDetails.id;
      setIsNewBooking(isNew);

      if (isNew) {
        setCustomerName('');
        setCustomerEmail('');
        setCustomerPhone('');
        setBookingNotes('');
        setMyNotes('');
      } else {
        setCustomerName(bookingDetails.extendedProps?.customerName || '');
        setCustomerEmail(bookingDetails.extendedProps?.customerEmail || '');
        setCustomerPhone(bookingDetails.extendedProps?.customerPhone || '');
        setBookingNotes(bookingDetails.extendedProps?.booking_notes || '');
        setMyNotes(bookingDetails.extendedProps?.my_notes || '');
      }
    }
  }, [bookingDetails]);

  const handleCreateBooking = async () => {
    try {
      if (!clientId) {
        toast.error('Client ID is required');
        return;
      }

      if (!customerName.trim()) {
        toast.error('Customer name is required');
        return;
      }

      setLoading(true);
      setActionError(null);

      const durationMinutes = Math.round(
        (new Date(bookingDetails.end) - new Date(bookingDetails.start)) / (1000 * 60)
      );

      const bookingData = {
        clientId: clientId,
        calendarId: calendarId,
        customerName: customerName,
        appointmentTime: bookingDetails.start.toISOString(),
        duration: durationMinutes,
        customerEmail: customerEmail,
        customerPhone: customerPhone,
        note: bookingNotes,
        status: 'scheduled',
        customerAddress: "",
        customerPostcode: ""
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bookings/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create booking');
      }

      const data = await response.json();
      toast.success('Booking created successfully!');

      if (onBookingCreated) {
        onBookingCreated(data.bookingId);
      }
      onClose();

    } catch (err) {
      console.error('Error creating booking:', err);
      toast.error(err.message || 'Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMyNotes = async () => {
    try {
      if (!isNewBooking && !bookingDetails?.id) {
        toast.error('No booking ID found');
        return;
      }

      setLoading(true);
      toast.error(null);

      if (isNewBooking) {
        toast.success('Notes saved locally');
      } else {
        const result = await addMyNotesAPI(bookingDetails.id, myNotes, user.id);
        toast.success('Notes updated successfully!');

        if (onStatusUpdate) {
          onStatusUpdate(bookingDetails.id, null, { my_notes: myNotes });
        }
      }
      onClose();
    } catch (err) {
      console.error('Error updating notes:', err);
      toast.error(err.message || 'Failed to update notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async () => {


    try {
      if (!bookingDetails?.id) return;

      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(`${process.env.REACT_APP_API_URL}/bookings/${bookingDetails.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to delete booking");

      toast.success("Booking deleted successfully!");

      // CLOSE FIRST
      setShowDeleteConfirm(false);
      onClose();

      onBookingDeleted();

    } catch (err) {
      console.error(err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };



  if (!isOpen || !bookingDetails) return null;

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const startTime = formatDateTime(bookingDetails.start);
  const endTime = formatDateTime(bookingDetails.end);

  const handleStatusUpdate = async (newStatus, reason = '') => {
    if (isNewBooking) {
      toast.error('Cannot update status for new booking. Please create the booking first.');
      return;
    }

    try {
      setLoading(true);
      setActionError(null);

      let result;
      if (newStatus === 'confirmed') {
        result = await acceptBooking(bookingDetails.id);
      } else if (newStatus === 'cancelled') {
        result = await cancelBooking(bookingDetails.id, reason);
      } else {
        result = await updateBookingStatus(bookingDetails.id, newStatus);
      }

      if (onStatusUpdate) {
        onStatusUpdate(bookingDetails.id, newStatus);
      }

      toast.success(`Booking ${newStatus} successfully!`);
      onClose();
    } catch (err) {
      console.error('Error updating booking status:', err);
      toast.error(err.message || 'Failed to update booking status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (isNewBooking) {
      toast.error('Please create the booking first before accepting it.');
      return;
    }
    setShowAcceptConfirm(true);
  };

  const handleCancel = () => {
    if (isNewBooking) {
      toast.error('Please create the booking first before cancelling it.');
      return;
    }
    setShowCancelReason(true);
  };

  const confirmAccept = () => {
    handleStatusUpdate('confirmed');
    setShowAcceptConfirm(false);
  };

  const confirmCancel = () => {
    handleStatusUpdate('cancelled', cancelReason);
    setShowCancelReason(false);
    setCancelReason('');
  };

  const cancelAction = () => {
    setShowAcceptConfirm(false);
    setShowCancelConfirm(false);
    setShowCancelReason(false);
    setCancelReason('');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'blocked': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Confirmation Dialog Component
  const ConfirmationDialog = ({
    isOpen,
    title,
    message,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    isDanger = false
  }) => {
    if (!isOpen) return null;

    return (
      <div className="confirmation-overlay">
        <div className="confirmation-dialog">
          <h4 className="confirmation-title">{title}</h4>
          <p className="confirmation-message">{message}</p>
          <div className="confirmation-buttons">
            <button
              onClick={onCancel}
              disabled={loading}
              className="confirmation-btn cancel-btn"
            >
              {cancelText || 'Cancel'}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`confirmation-btn ${isDanger ? 'danger-btn' : 'confirm-btn'}`}
            >
              {loading ? 'Processing...' : confirmText || 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  };



  const CancelReasonDialog = ({
    isOpen,
    onConfirm,
    onCancel
  }) => {
    if (!isOpen) return null;

    return (
      <div className="confirmation-overlay">
        <div className="confirmation-dialog reason-dialog">
          <h4 className="confirmation-title">Cancel Booking</h4>
          <p className="confirmation-message">
            Are you sure you want to cancel this booking?
            <br />
            <small className="text-gray-600">(Optional) Please provide a reason:</small>
          </p>
          <div className="reason-input-group">
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter cancellation reason (optional)"
              className="reason-textarea"
              rows="3"
              disabled={loading}
            />
          </div>
          <div className="confirmation-buttons">
            <button
              onClick={onCancel}
              disabled={loading}
              className="confirmation-btn cancel-btn"
            >
              Keep Booking
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="confirmation-btn danger-btn"
            >
              {loading ? 'Processing...' : 'Cancel Booking'}
            </button>
          </div>
        </div>
      </div>
    );
  };


  const ConfirmDeleteBookingDialog = ({
    isOpen,
    bookingTitle,
    bookingTime,
    onConfirmDelete,
    onCancel,
    loading = false
  }) => {
    if (!isOpen) return null;

    return (
      <div className="confirmation-overlay">
        <div className="confirmation-dialog">
          <h4 className="confirmation-title">Delete Booking</h4>

          <p className="confirmation-message">
            Are you sure you want to delete this booking?
          </p>

          {(bookingTitle || bookingTime) && (
            <div className="confirmation-details">
              {bookingTitle && <p><strong>{bookingTitle}</strong></p>}
              {bookingTime && <p>{bookingTime}</p>}
            </div>
          )}

          <div className="confirmation-buttons">
            <button
              onClick={onCancel}
              disabled={loading}
              className="confirmation-btn cancel-btn"
            >
              Cancel
            </button>

            <button
              onClick={onConfirmDelete}
              disabled={loading}
              className="confirmation-btn danger-btn"
            >
              {loading ? "Deleting..." : "Delete Booking"}
            </button>
          </div>
        </div>
      </div>
    );
  };





  return (
    <>
      <div className="booking-popup-overlay">
        <div className="booking-popup-container poppin">
          {/* <pre>{JSON.stringify(bookingDetails)}</pre> */}
          <div className="popup-header">
            <h2 className="popup-title">
              <i className={`fas fa-${isNewBooking ? 'calendar-plus' : 'calendar-alt'} me-2`}></i>
              {isNewBooking ? 'Create New Booking' : 'Booking Details'}
            </h2>
            <button
              onClick={onClose}
              className="close-button"
              disabled={loading}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Error Message */}
          {actionError && (
            <div className="error-message">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  <strong>Error:</strong> {actionError}
                </div>
                <button
                  onClick={() => setActionError(null)}
                  className="error-close-btn"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          )}

          {/* Booking Details */}
          <div className="booking-details">
            {/* Customer Information Section */}
            <div className="detail-section">
              <div className="row g-4">
                {/* Left Column - Customer Info */}
                <div className="col-lg-6">
                  <div className="booking-form-section">
                    <h4 className="section-title mb-4">
                      <i className="fas fa-user-circle me-2"></i>
                      Customer Information
                    </h4>

                    {isNewBooking ? (
                      <div className="form-fields">
                        <div className="form-group">
                          <label className="form-label">
                            <i className="fas fa-user me-2"></i>
                            Customer Name *
                          </label>
                          <div className="input-with-icon">
                            <i className="input-icon fas fa-user"></i>
                            <input
                              type="text"
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              placeholder="Enter customer name"
                              className="form-control"
                              disabled={loading}
                              required
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <i className="fas fa-envelope me-2"></i>
                            Email Address
                          </label>
                          <div className="input-with-icon">
                            <i className="input-icon fas fa-envelope"></i>
                            <input
                              type="email"
                              value={customerEmail}
                              onChange={(e) => setCustomerEmail(e.target.value)}
                              placeholder="Enter customer email"
                              className="form-control"
                              disabled={loading}
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <i className="fas fa-phone me-2"></i>
                            Phone Number
                          </label>
                          <div className="input-with-icon">
                            <i className="input-icon fas fa-phone"></i>
                            <input
                              type="tel"
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              placeholder="Enter customer phone"
                              className="form-control"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="customer-info-display">
                        <div className="info-item">
                          <div className="info-label">
                            <i className="fas fa-user me-2"></i>
                            Name:
                          </div>
                          <div className="info-value">{customerName || 'N/A'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">
                            <i className="fas fa-envelope me-2"></i>
                            Email:
                          </div>
                          <div className="info-value">{customerEmail || 'N/A'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">
                            <i className="fas fa-phone me-2"></i>
                            Phone:
                          </div>
                          <div className="info-value">{customerPhone || 'N/A'}</div>
                        </div>
                        {bookingDetails?.createdAt && (
                          <p class="label-glass-info">
                            Created: {new Date(bookingDetails.createdAt).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Notes & Actions */}
                <div className="col-lg-6">
                  <div className="notes-actions-section">
                    <div className="form-group mb-4">
                      <label className="form-label">
                        <i className="fas fa-sticky-note me-2"></i>
                        Booking Notes
                      </label>
                      {isNewBooking ? (
                        <div className="input-with-icon">
                          <i className="input-icon fas fa-sticky-note"></i>
                          <textarea
                            value={bookingNotes}
                            onChange={(e) => setBookingNotes(e.target.value)}
                            placeholder="Add booking notes here..."
                            className="form-control"
                            disabled={loading}
                            rows={3}
                          />
                        </div>
                      ) : bookingDetails.extendedProps?.booking_notes ? (
                        <div className="notes-display">
                          <p>{bookingDetails.extendedProps.booking_notes}</p>
                        </div>
                      ) : (
                        <div className="no-notes">
                          <i className="fas fa-sticky-note me-2"></i>
                          No booking notes
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons-section">
                      {isNewBooking ? (
                        <button
                          onClick={handleCreateBooking}
                          disabled={loading || !customerName.trim()}
                          className="btn-create-booking"
                        >
                          <i className="fas fa-calendar-check me-2"></i>
                          {loading ? 'Creating...' : 'Create Booking'}
                        </button>
                      ) : (
                        <>
                        <div className="status-actions">
                          

                          {bookingDetails.extendedProps?.status === 'scheduled' && (
                            <>
                              <button
                                onClick={handleAccept}
                                disabled={loading}
                                className="btn-accept"
                              >
                                <i className="fas fa-check-circle me-2"></i>
                                Accept Booking
                              </button>
                              <button
                                onClick={handleCancel}
                                disabled={loading}
                                className="btn-glass-warning"
                              >
                                <i className="fas fa-times-circle me-2"></i>
                                Cancel Booking
                              </button>
                            </>
                          )}

                          {bookingDetails.extendedProps?.status === 'confirmed' && (
                            <button
                              onClick={handleCancel}
                              disabled={loading}
                              className="btn-glass-warning"
                            >
                              <i className="fas fa-times-circle me-2"></i>
                              Cancel Booking
                            </button>
                          )}

                          {bookingDetails.extendedProps?.status === 'cancelled' && (
                            <div className="status-badge cancelled">
                              <i className="fas fa-ban me-2"></i>
                              Booking Cancelled
                            </div>
                          )}
                          {bookingDetails.extendedProps?.status === 'confirmed' && (
                            <div className="status-badge confirmed">
                              <i className="fas fa-ban me-2"></i>
                              Booking Confirmed
                            </div>
                          )}
                        </div>
                        <div className='mt-3'>
                          <button
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={loading}
                            className="btn-glass-danger"
                          >
                            <i className="fas fa-trash me-2"></i>
                            Delete Booking
                          </button>
                          </div></>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Information Section */}
            <div className="detail-section booking-info-section">
              <h4 className="section-title mb-4">
                <i className="fas fa-info-circle me-2"></i>
                Booking Information
              </h4>

              <div className="row g-3">
                <div className="col-md-6 col-lg-3">
                  <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-calendar-day"></i>
                    </div>
                    <div className="info-content">
                      <div className="info-label">Date</div>
                      <div className="info-value">{startTime.date}</div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="info-content">
                      <div className="info-label">Time</div>
                      <div className="info-value">
                        {startTime.time} - {endTime.time}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-hourglass-half"></i>
                    </div>
                    <div className="info-content">
                      <div className="info-label">Duration</div>
                      <div className="info-value">
                        {Math.round((new Date(bookingDetails.end) - new Date(bookingDetails.start)) / (1000 * 60))} minutes
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-id-badge"></i>
                    </div>
                    <div className="info-content">
                      <div className="info-label">Booking ID</div>
                      <div className="info-value">
                        {isNewBooking ? 'New' : `#${bookingDetails.id}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Display */}
              {!isNewBooking && bookingDetails.extendedProps?.status && (
                <div className="status-display mt-4">
                  <div className="status-label">Status:</div>
                  <span className={`status-badge ${getStatusColor(bookingDetails.extendedProps?.status)}`}>
                    <i className={`fas fa-${bookingDetails.extendedProps?.status === 'confirmed' ? 'check-circle' :
                      bookingDetails.extendedProps?.status === 'cancelled' ? 'times-circle' :
                        bookingDetails.extendedProps?.status === 'completed' ? 'check-double' : 'clock'
                      } me-2`}></i>
                    {bookingDetails.extendedProps?.status}
                  </span>
                </div>
              )}
            </div>

            {/* My Notes Section */}
            <div className="detail-section my-notes-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="section-title mb-0">
                  <i className="fas fa-edit me-2"></i>
                  My Notes
                </h4>
                <button
                  onClick={handleAddMyNotes}
                  disabled={loading || (!isNewBooking && !bookingDetails?.id)}
                  className="btn-save-notes"
                >
                  <i className="fas fa-save me-2"></i>
                  {loading ? 'Saving...' : isNewBooking ? 'Save Notes' : 'Update Notes'}
                </button>
              </div>

              <div className="notes-textarea-container">
                <textarea
                  value={myNotes}
                  onChange={(e) => setMyNotes(e.target.value)}
                  placeholder="Add your private notes here..."
                  className="form-control notes-textarea"
                  disabled={loading}
                  rows={4}
                  maxLength={500}
                />
                <div className="notes-counter">
                  {myNotes.length}/500 characters
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteBookingDialog
        isOpen={showDeleteConfirm}
        bookingTitle={bookingDetails?.title}
        bookingTime={
          bookingDetails
            ? `${new Date(bookingDetails.start).toLocaleString("en-GB")} - ${new Date(bookingDetails.end).toLocaleString("en-GB")}`
            : ""
        }
        loading={loading}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirmDelete={handleDeleteBooking}
      />



      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showAcceptConfirm}
        title="Accept Booking"
        message="Are you sure you want to accept this booking?"
        confirmText="Yes, Accept"
        cancelText="No, Go Back"
        onConfirm={confirmAccept}
        onCancel={cancelAction}
        isDanger={false}
      />

      <CancelReasonDialog
        isOpen={showCancelReason}
        onConfirm={confirmCancel}
        onCancel={cancelAction}
      />
    </>
  );
};

export default PopupBooking;